/* eslint-disable react-doctor/no-giant-component */
import { Button } from "@shadcn/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@shadcn/ui/card";
import { createFileRoute, Link } from "@tanstack/react-router";
/* eslint-disable react/no-giant-component */
import {
  AlertTriangle,
  ArrowLeft,
  Clock,
  FileText,
  Globe,
  HeadphonesIcon,
  Mail,
  MapPin,
  Phone,
  Trash2,
} from "lucide-react";
import Logo from "@/src/presentation/components/logo";
import { useBrand } from "@/src/brand";

export const Route = createFileRoute("/_public/support/")({
  component: SupportPage,
});

function SupportPage() {
  const brand = useBrand();
  return (
    <div className="flex min-h-screen items-center justify-center bg-slate-50 p-4">
      <div className="w-full max-w-4xl">
        <Logo />

        {/* Main Support Card */}
        <Card className="mb-6 border-brand-accent-subtle shadow-lg">
          <CardHeader className="text-center">
            <CardTitle className="mb-2 text-3xl font-semibold text-brand-accent">
              <HeadphonesIcon className="mr-3 inline-block size-8" />
              Customer Support
            </CardTitle>
            <CardDescription className="text-lg">
              We&apos;re here to help you with the Water Device Management System
            </CardDescription>
          </CardHeader>
        </Card>

        {/* Contact Methods Grid */}
        <div className="mb-6 grid gap-6 md:grid-cols-2">
          {/* Email Support */}
          <Card className="transition-shadow hover:shadow-md">
            <CardHeader>
              <CardTitle className="flex items-center text-xl">
                <Mail className="mr-2 size-5 text-brand-accent" />
                Email Support
              </CardTitle>
              <CardDescription>Get detailed help via email</CardDescription>
            </CardHeader>
            <CardContent>
              <p className="text-muted-foreground mb-4">
                Send us your questions and we&apos;ll respond within 24 hours
              </p>
              <Button asChild className="w-full bg-brand-accent hover:bg-brand-accent-hover">
                <a href={`mailto:${brand.strings.supportEmail}`}>Send Email</a>
              </Button>
            </CardContent>
          </Card>

          {/* Account Deletion */}
          <Card className="border-red-200 bg-red-50 transition-shadow hover:shadow-md">
            <CardHeader>
              <CardTitle className="flex items-center text-xl">
                <Trash2 className="mr-2 size-5 text-red-600" />
                Account Deletion
              </CardTitle>
              <CardDescription className="text-red-700">
                Permanently delete your account and all data
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="mb-4 flex items-start rounded-md border border-red-200 bg-red-100 px-3 py-2 text-red-700">
                <AlertTriangle className="mt-0.5 mr-2 size-4 flex-shrink-0" />
                <div>
                  <p className="text-xs font-medium">Important: This action cannot be undone</p>
                  <p className="mt-1 text-xs">
                    Note: Devices are not actually removed from the system, only your access is
                    revoked
                  </p>
                </div>
              </div>
              <Button
                variant="destructive"
                className="w-full bg-red-600 hover:bg-red-700"
                onClick={() => {
                  window.location.href = "/dashboard/settings?tab=security#account-deletion";
                }}
              >
                Go to Account Deletion
              </Button>
            </CardContent>
          </Card>

          {/* Phone Support */}
          <Card className="transition-shadow hover:shadow-md">
            <CardHeader>
              <CardTitle className="flex items-center text-xl">
                <Phone className="mr-2 size-5 text-green-600" />
                Phone Support
              </CardTitle>
              <CardDescription>Speak directly with our support team</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="mb-4 space-y-2">
                <div className="flex items-center">
                  <Clock className="text-muted-foreground mr-2 size-4" />
                  <span className="text-sm">Mon-Fri: 9:00 AM - 6:00 PM AEST</span>
                </div>
                <div className="flex items-center">
                  <MapPin className="text-muted-foreground mr-2 size-4" />
                  <span className="text-sm">Australia</span>
                </div>
              </div>
              <Button asChild className="w-full bg-green-600 hover:bg-green-700">
                <a href="tel:+61-1800-123-456">Call +61 1800 123 456</a>
              </Button>
            </CardContent>
          </Card>

          {/* Documentation */}
          <Card className="transition-shadow hover:shadow-md">
            <CardHeader>
              <CardTitle className="flex items-center text-xl">
                <FileText className="mr-2 size-5 text-orange-600" />
                Documentation
              </CardTitle>
              <CardDescription>Browse our comprehensive guides</CardDescription>
            </CardHeader>
            <CardContent>
              <p className="text-muted-foreground mb-4">
                Find answers in our user manual and FAQ section
              </p>
              <Button
                variant="outline"
                className="w-full border-orange-600 text-orange-600 hover:bg-orange-50"
                onClick={() => {
                  alert("Documentation section coming soon!");
                }}
              >
                View Documentation
              </Button>
            </CardContent>
          </Card>
        </div>

        {/* App Store Information */}
        <Card className="mb-6 border-green-100 bg-green-50 shadow-lg">
          <CardHeader>
            <CardTitle className="flex items-center text-xl text-green-800">
              <Globe className="mr-2 size-5" />
              App Store Support
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div>
                <h3 className="mb-2 font-semibold text-green-800">Download Issues?</h3>
                <p className="mb-3 text-sm text-green-700">
                  If you&apos;re experiencing problems downloading or installing the app from the
                  App Store, please try:
                </p>
                <ul className="ml-4 list-inside list-disc space-y-1 text-sm text-green-700">
                  <li>Check your internet connection</li>
                  <li>Ensure you have sufficient storage space</li>
                  <li>Restart your device and try again</li>
                  <li>Update your device&apos;s operating system</li>
                </ul>
              </div>

              <div>
                <h3 className="mb-2 font-semibold text-green-800">App Not Working?</h3>
                <p className="mb-3 text-sm text-green-700">
                  For app-specific issues, please contact us using any of the methods above and
                  include:
                </p>
                <ul className="ml-4 list-inside list-disc space-y-1 text-sm text-green-700">
                  <li>Your device model and operating system version</li>
                  <li>App version number</li>
                  <li>Detailed description of the issue</li>
                  <li>Steps to reproduce the problem</li>
                </ul>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Account Deletion Support */}
        <Card className="mb-6 border-red-100 bg-red-50 shadow-lg">
          <CardHeader>
            <CardTitle className="flex items-center text-xl text-red-800">
              <Trash2 className="mr-2 size-5" />
              Account Deletion
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="flex items-start rounded-md border border-red-200 bg-red-100 px-4 py-3 text-red-700">
                <AlertTriangle className="mt-0.5 mr-2 size-5 flex-shrink-0" />
                <div>
                  <p className="font-medium">Important Notice</p>
                  <p className="mt-1 text-sm">
                    Account deletion is permanent and cannot be undone. All your data, devices, and
                    settings will be permanently removed.
                  </p>
                </div>
              </div>

              <div>
                <h3 className="mb-2 font-semibold text-red-800">Need to Delete Your Account?</h3>
                <p className="mb-3 text-sm text-red-700">
                  If you wish to delete your account, you can do so through your account settings.
                  Please note:
                </p>
                <ul className="ml-4 list-inside list-disc space-y-1 text-sm text-red-700">
                  <li>Account deletion is scheduled for 15 minutes after request</li>
                  <li>You can cancel the deletion during this waiting period</li>
                  <li>All your personal data and measurements will be permanently lost</li>
                  <li>
                    Devices are not actually removed from the system - only your access is revoked
                  </li>
                  <li>This action cannot be reversed once completed</li>
                </ul>
              </div>

              <div>
                <h3 className="mb-2 font-semibold text-red-800">How to Delete Your Account</h3>

                <div className="mb-4">
                  <h4 className="mb-2 font-medium text-red-800">On Web Dashboard:</h4>
                  <ol className="ml-4 list-inside list-decimal space-y-1 text-sm text-red-700">
                    <li>Log in to your account</li>
                    <li>Go to Settings in your dashboard</li>
                    <li>Click &quot;Delete Account&quot;</li>
                    <li>
                      In the &quot;Are you sure?&quot; popup, click &quot;Delete&quot; to confirm
                    </li>
                  </ol>
                </div>

                <div>
                  <h4 className="mb-2 font-medium text-red-800">On Mobile App:</h4>
                  <ol className="ml-4 list-inside list-decimal space-y-1 text-sm text-red-700">
                    <li>Open the app and log in to your account</li>
                    <li>Tap on the &quot;Settings&quot; tab at the bottom of the screen</li>
                    <li>Select &quot;Delete account&quot; from the settings menu</li>
                    <li>
                      In the &quot;Are you sure?&quot; popup, tap &quot;Delete&quot; to confirm
                    </li>
                  </ol>
                </div>
              </div>

              <div>
                <h3 className="mb-2 font-semibold text-red-800">Need Help?</h3>
                <p className="mb-3 text-sm text-red-700">
                  If you have questions about account deletion or need assistance, please contact
                  our support team using any of the methods above before proceeding.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* FAQ Section */}
        <Card className="mb-6 shadow-lg">
          <CardHeader>
            <CardTitle className="text-xl">Frequently Asked Questions</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div>
                <h3 className="mb-2 font-semibold">How do I reset my password?</h3>
                <p className="text-muted-foreground text-sm">
                  Click on &quot;Forgot Password&quot; on the login page and follow the instructions
                  sent to your email.
                </p>
              </div>

              <div>
                <h3 className="mb-2 font-semibold">How do I add a new water device?</h3>
                <p className="text-muted-foreground text-sm">
                  Navigate to the Devices section in your dashboard and click &quot;Add New
                  Device&quot;. Follow the setup wizard to connect your device.
                </p>
              </div>

              <div>
                <h3 className="mb-2 font-semibold">Why can&apos;t I see my device readings?</h3>
                <p className="text-muted-foreground text-sm">
                  Ensure your device is connected to the internet and has been properly configured.
                  Check the device status in your dashboard.
                </p>
              </div>

              <div>
                <h3 className="mb-2 font-semibold">How do I update my account information?</h3>
                <p className="text-muted-foreground text-sm">
                  Go to Settings in your dashboard to update your profile information and account
                  preferences.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Contact Information Card */}
        <Card className="mb-6 border-brand-accent-subtle bg-brand-accent-light shadow-lg">
          <CardHeader>
            <CardTitle className="text-xl text-brand-accent-hover">Contact Information</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid gap-4 text-sm md:grid-cols-2">
              <div>
                <h4 className="mb-2 font-semibold text-brand-accent-hover">Business Hours</h4>
                <p className="text-brand-accent-hover">Monday - Friday: 9:00 AM - 6:00 PM AEST</p>
                <p className="text-brand-accent-hover">Saturday: 10:00 AM - 4:00 PM AEST</p>
                <p className="text-brand-accent-hover">Sunday: Closed</p>
              </div>

              <div>
                <h4 className="mb-2 font-semibold text-brand-accent-hover">Emergency Support</h4>
                <p className="text-brand-accent-hover">
                  For critical system issues outside business hours:
                </p>
                <p className="font-semibold text-brand-accent-hover">
                  +61 1800 URGENT (1800 874 368)
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Back to App Button */}
        <div className="flex justify-center">
          <Button asChild variant="outline" className="px-8">
            <Link to="/login">
              <ArrowLeft className="mr-2 size-4" />
              Back to Login
            </Link>
          </Button>
        </div>
      </div>
    </div>
  );
}
